/**
 * 설비 캐시 작업
 */

import {
  getRecord,
  putRecord,
  deleteRecord,
  clearStore,
  getRecordsByIndex,
  getUniqueIndexKeys,
} from "./index";
import type { CachedMachine } from "./types";

const STORE_NAME = "machines";

/**
 * 설비 복합 키 생성
 */
function getMachineKey(projectId: string, docKey: string, machineId: string): string {
  return `${projectId}-${docKey}-${machineId}`;
}

/**
 * 설비를 IndexedDB에 캐시
 */
export async function cacheMachine(
  machine: Omit<CachedMachine, "cachedAt"> & { cachedAt?: number }
): Promise<void> {
  const cachedMachine: CachedMachine = {
    ...machine,
    cachedAt: machine.cachedAt ?? Date.now(),
    syncStatus: machine.syncStatus ?? "synced",
  };
  await putRecord(STORE_NAME, cachedMachine);
}

/**
 * 여러 설비를 한 번에 캐시
 */
export async function cacheMachines(
  machines: Array<Omit<CachedMachine, "cachedAt" | "syncStatus">>
): Promise<void> {
  const now = Date.now();
  for (const machine of machines) {
    await cacheMachine({
      ...machine,
      cachedAt: now,
      syncStatus: "synced",
    });
  }
}

/**
 * projectId, docKey, machineId로 캐시된 설비 조회
 */
export async function getCachedMachine(
  projectId: string,
  docKey: string,
  machineId: string
): Promise<CachedMachine | undefined> {
  const key = getMachineKey(projectId, docKey, machineId);
  return getRecord<CachedMachine>(STORE_NAME, key);
}

/**
 * 프로젝트ID로 해당 프로젝트의 모든 설비 조회
 */
export async function getCachedMachinesByProject(projectId: string): Promise<CachedMachine[]> {
  return getRecordsByIndex<CachedMachine>(STORE_NAME, "by-projectId", projectId);
}

/**
 * 프로젝트ID와 docKey로 해당 설비 그룹의 설비 목록 조회
 */
export async function getCachedMachinesByDocKey(
  projectId: string,
  docKey: string
): Promise<CachedMachine[]> {
  const allMachines = await getCachedMachinesByProject(projectId);
  return allMachines
    .filter((m) => m.group.docKey === docKey)
    .sort((a, b) => a.machine._id.localeCompare(b.machine._id));
}

/**
 * 캐시된 설비 삭제
 */
export async function removeCachedMachine(
  projectId: string,
  docKey: string,
  machineId: string
): Promise<void> {
  const key = getMachineKey(projectId, docKey, machineId);
  await deleteRecord(STORE_NAME, key);
}

/**
 * 모든 설비 캐시 삭제
 */
export async function clearMachineCache(): Promise<void> {
  await clearStore(STORE_NAME);
}

/**
 * 설비 로컬 변경사항 저장 (오프라인 수정)
 */
export async function updateCachedMachine(
  projectId: string,
  docKey: string,
  machineId: string,
  changes: Partial<CachedMachine>
): Promise<void> {
  const existing = await getCachedMachine(projectId, docKey, machineId);
  if (existing) {
    await cacheMachine({
      ...existing,
      ...changes,
      syncStatus: "pending",
      localChanges: {
        ...existing.localChanges,
        ...changes,
        updatedAt: Date.now(),
      },
      cachedAt: Date.now(),
    });
  }
}

/**
 * 캐시된 설비가 있는 프로젝트 ID 목록 조회
 * 오프라인 상태에서 접근 가능한 프로젝트를 식별하는 데 사용
 * 성능 최적화: 전체 레코드 로드 대신 인덱스 키만 순회
 */
export async function getCachedProjectIds(): Promise<Set<string>> {
  return getUniqueIndexKeys(STORE_NAME, "by-projectId");
}
